/**
 * Mapeamento de mensagens do backend (pt-BR) → chaves i18n
 * 
 * O backend sempre retorna mensagens em pt-BR.
 * Quando o locale do frontend é pt-BR, usamos a mensagem original da API (mais precisa).
 * Para en-US/es, buscamos a tradução via este mapa.
 */
import { t, getCurrentLocale } from '@/utils/i18nHelper'

/**
 * Dicionário: mensagem pt-BR do backend → chave i18n
 * Usa startsWith para match parcial em mensagens dinâmicas
 */
const exactMap = {
  // === Auth ===
  'Email e senha são obrigatórios': 'apiMessages.emailPasswordRequired',
  'Credenciais inválidas': 'apiMessages.invalidCredentials',
  'Não autenticado': 'apiMessages.notAuthenticated',
  'Token inválido': 'apiMessages.invalidToken',
  'Sua conta de revendedor está inativa. Entre em contato com o suporte.': 'apiMessages.resellerInactive',
  'Sua assinatura de revendedor expirou. Renove para continuar.': 'apiMessages.resellerExpired',
  'Dados de revendedor não encontrados': 'apiMessages.resellerNotFound',
  'Login realizado com sucesso': 'apiMessages.loginSuccess',
  'Logout realizado com sucesso': 'apiMessages.logoutSuccess',

  // === Profile ===
  'Senha atual é obrigatória': 'apiMessages.currentPasswordRequired',
  'Nova senha é obrigatória': 'apiMessages.newPasswordRequired',
  'Senha atual incorreta': 'apiMessages.currentPasswordWrong',
  'Senha alterada com sucesso': 'apiMessages.passwordChanged',
  'Nenhum arquivo enviado ou erro no upload': 'apiMessages.noFileUploaded',
  'Arquivo muito grande. Máximo: 1MB': 'apiMessages.fileTooLarge',
  'Arquivo muito grande. Máximo: 3MB': 'apiMessages.fileTooLarge3mb',
  'Tipo de arquivo inválido. Use JPG ou PNG': 'apiMessages.invalidFileType',
  'Erro ao processar imagem': 'apiMessages.imageProcessingError',
  'Imagem muito grande. Máximo: 512x512 pixels': 'apiMessages.imageTooLarge',
  'Avatar atualizado com sucesso': 'apiMessages.avatarUpdated',
  'Avatar removido com sucesso': 'apiMessages.avatarRemoved',

  // === Accounts ===
  'Conta criada com sucesso': 'apiMessages.accountCreated',
  'Conta atualizada com sucesso': 'apiMessages.accountUpdated',
  'Conta deletada com sucesso': 'apiMessages.accountDeleted',
  'Conta renovada com sucesso': 'apiMessages.accountRenewed',
  'Conta criada a partir do plano': 'apiMessages.accountCreatedFromPlan',
  'Campos plan_id e category_id são obrigatórios': 'apiMessages.planCategoryRequired',
  'Nenhum ID válido fornecido': 'apiMessages.noValidIds',
  'Erro ao listar contas': 'apiMessages.errorListingAccounts',
  'Erro ao atualizar conta': 'apiMessages.errorUpdatingAccount',
  'Erro ao deletar conta': 'apiMessages.errorDeletingAccount',

  // === Plans ===
  'Plano criado com sucesso': 'apiMessages.planCreated',
  'Plano atualizado com sucesso': 'apiMessages.planUpdated',
  'Plano deletado com sucesso': 'apiMessages.planDeleted',
  'Você não tem permissão para editar este plano': 'apiMessages.noPermissionEditPlan',
  'Você não tem permissão para deletar este plano': 'apiMessages.noPermissionDeletePlan',

  // === Resellers ===
  'Revendedor não encontrado': 'apiMessages.resellerNotFound',
  'Revendedor não possui categoria vinculada': 'apiMessages.resellerNoCategory',
  'Erro ao buscar dados': 'apiMessages.errorFetchingData',

  // === Admin ===
  'Usuário deve ter 3-50 caracteres alfanuméricos': 'apiMessages.usernameInvalid',
  'Email inválido': 'apiMessages.emailInvalid',
  'Senha deve ter no mínimo 6 caracteres': 'apiMessages.passwordTooShort',
  'Usuário já existe': 'apiMessages.usernameExists',
  'Email já cadastrado': 'apiMessages.emailExists',
  'Administrador criado com sucesso': 'apiMessages.adminCreated',
  'Administrador atualizado com sucesso': 'apiMessages.adminUpdated',
  'Administrador deletado com sucesso': 'apiMessages.adminDeleted',
  'Não é possível alterar role de super_admin': 'apiMessages.cannotChangeSuperAdmin',
  'Não é possível deletar super_admin': 'apiMessages.cannotDeleteSuperAdmin',
  'Não é possível deletar sua própria conta': 'apiMessages.cannotDeleteSelf',
  'Você não pode desativar sua própria conta': 'apiMessages.cannotDeactivateSelf',
  'Não é possível desativar super_admin': 'apiMessages.cannotDeactivateSuperAdmin',
  'Erro ao listar administradores': 'apiMessages.errorListingAdmins',

  // === Coupons ===
  'Código do cupom é obrigatório': 'apiMessages.couponCodeRequired',
  'Desconto inválido': 'apiMessages.invalidDiscount',
  'Cupom com este código já existe': 'apiMessages.couponExists',
  'Cupom criado com sucesso': 'apiMessages.couponCreated',
  'Cupom inválido ou expirado': 'apiMessages.couponInvalid',

  // === CheckUser ===
  'Usuário obrigatório': 'apiMessages.usernameRequired',
  'Usuário é obrigatório': 'apiMessages.usernameRequired',
  'Usuário deve ter 3-32 caracteres (apenas letras e números)': 'viewMessages.payment.usernameValidation',
  'Usuário já está em uso. Escolha outro.': 'publicPayment.usernameTaken',
  'Usuário ou email já existe': 'apiMessages.usernameOrEmailExists',

  // === Servers ===
  'Servidor criado com sucesso': 'apiMessages.serverCreated',
  'Servidor atualizado com sucesso': 'apiMessages.serverUpdated',
  'Servidor deletado com sucesso': 'apiMessages.serverDeleted',
  'Sincronização iniciada. Será notificado quando terminar.': 'apiMessages.syncStarted',
  'ID do servidor inválido': 'apiMessages.invalidServerId',
  'Utilizador não identificado': 'apiMessages.userNotIdentified',
  'Servidor não encontrado': 'apiMessages.serverNotFound',
  'Tarefa não encontrada ou expirada': 'apiMessages.jobNotFound',
  'job_id é obrigatório': 'apiMessages.jobIdRequired',

  // === Categories ===
  'Categoria criada com sucesso': 'apiMessages.categoryCreated',
  'Categoria atualizada com sucesso': 'apiMessages.categoryUpdated',
  'Categoria deletada com sucesso': 'apiMessages.categoryDeleted',

  // === Settings ===
  'Configurações salvas com sucesso': 'apiMessages.settingsSaved',
  'Backup realizado com sucesso': 'apiMessages.backupSuccess',

  // === Reseller Subscription/Credits ===
  'Revendedor criado com sucesso': 'apiMessages.resellerCreated',
  'Revendedor atualizado com sucesso': 'apiMessages.resellerUpdated',
  'Revendedor deletado com sucesso': 'apiMessages.resellerDeleted',

  // === Test Account ===
  'Conta de teste criada com sucesso': 'apiMessages.testAccountCreated',
  'Sua assinatura expirou. Renove para continuar.': 'apiMessages.subscriptionExpired',

  // === Genéricos ===
  'Usuário não encontrado': 'apiMessages.userNotFound',
  'Apenas revendedores': 'apiMessages.resellersOnly',
  'Erro': 'apiMessages.error',
}

/**
 * Prefixos para match parcial (mensagens que variam)
 */
const prefixMap = [
  { prefix: 'Sua conta está ', key: 'apiMessages.accountStatusIs' },
  { prefix: 'Sua conta de revendedor está ', key: 'apiMessages.resellerAccountStatusIs' },
  { prefix: 'Sua assinatura de revendedor expirou em ', key: 'apiMessages.resellerExpiredAt' },
  { prefix: 'Sem créditos disponíveis.', key: 'apiMessages.noCreditsAvailable' },
  { prefix: 'Limite de logins atingido.', key: 'apiMessages.loginLimitReached' },
  { prefix: 'Nenhum servidor encontrado na categoria', key: 'apiMessages.noServersInCategory' },
  { prefix: 'Erro ao deletar contas:', key: 'apiMessages.errorDeletingAccounts' },
  { prefix: 'Erro no login:', key: 'apiMessages.loginError' },
  { prefix: 'Erro ao buscar usuário:', key: 'apiMessages.errorFetchingUser' },
  { prefix: 'Erro ao renovar token:', key: 'apiMessages.errorRenewingToken' },
  { prefix: 'Erro ao atualizar perfil:', key: 'apiMessages.errorUpdatingProfile' },
  { prefix: 'Erro ao listar planos:', key: 'apiMessages.errorListingPlans' },
  { prefix: 'IDs inválidos.', key: 'apiMessages.invalidIds' },
  { prefix: 'Revendedores tipo CRÉDITO', key: 'apiMessages.creditResellerRestriction' },
  { prefix: 'Digite uma nova senha', key: 'apiMessages.enterNewPassword' },
]

/**
 * Traduz uma mensagem da API.
 * - Se locale é pt-BR, retorna a mensagem original (backend já é pt-BR)
 * - Se locale é outro, busca no mapa e traduz via i18n
 * - Se não encontrar no mapa, retorna a mensagem original
 * 
 * @param {string|undefined} message - Mensagem vinda da API
 * @returns {string|undefined} Mensagem traduzida ou original
 */
export function translateApiMessage(message) {
  if (!message) return undefined

  const locale = getCurrentLocale()
  
  // pt-BR: usar mensagem original do backend (mais precisa)
  if (locale === 'pt-BR' || locale === 'pt') {
    return message
  }

  // Buscar no mapa exato
  const exactKey = exactMap[message]
  if (exactKey) {
    return t(exactKey)
  }

  // Buscar no mapa de prefixos
  for (const { prefix, key } of prefixMap) {
    if (message.startsWith(prefix)) {
      return t(key)
    }
  }

  // Não encontrou: retornar mensagem original
  return message
}
